import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from "@angular/core";
import { ReminderPoolFacade } from "../store/facade";
import { Observable, ReplaySubject, Subject, combineLatest } from "rxjs";

import { ReminderFolder } from "../store";
import { take, takeUntil } from "rxjs/operators";
import { FormControl, FormGroup } from "@angular/forms";
import { FlatTreeControl } from "@angular/cdk/tree";
import { MatTreeFlatDataSource, MatTreeFlattener } from "@angular/material/tree";
import { ActivityItemDefinition, ReminderFolderFlatNode, ReminderFolderNode } from "../../model";

@Component({
    selector: "reminder-picker",
    templateUrl: "./reminder-picker.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReminderPickerComponent implements OnInit, OnDestroy {
    private _unsubscribeAll = new Subject();

    filteredFolders$: ReplaySubject<ReminderFolder[]> = new ReplaySubject<ReminderFolder[]>(1);

    filteredForms$: ReplaySubject<ActivityItemDefinition[]> = new ReplaySubject<ActivityItemDefinition[]>(1);

    reminderFolders: ReminderFolderNode[] = [];

    flatNodeMap: Map<ReminderFolderFlatNode, ReminderFolderNode> = new Map();

    nestedNodeMap: Map<string, ReminderFolderFlatNode> = new Map();

    searchForm: FormGroup = new FormGroup({
        text: new FormControl(""),
    });

    private _transformer = (node: ReminderFolderNode, level) => {
        const flatNode = new ReminderFolderFlatNode();
        flatNode.level = level;
        flatNode.expandable = node.children && node.children?.length > 0;
        flatNode.title = node.title;
        flatNode.parentId = node.parentId;
        flatNode.data = node.data;
        this.flatNodeMap.set(flatNode, node);
        this.nestedNodeMap.set(node.title, flatNode);
        return flatNode;
    };

    treeControl = new FlatTreeControl<ReminderFolderFlatNode>(
        (node) => node.level,
        (node) => node.expandable
    );

    treeFlattener = new MatTreeFlattener(
        this._transformer,
        (node) => node.level,
        (node) => node.expandable,
        (node) => node.children
    );

    dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    hasChild = (_: number, node: ReminderFolderFlatNode) => node.expandable;

    isLoading$: Observable<boolean>;

    folders$: Observable<ReminderFolder[]>;

    items$: Observable<ActivityItemDefinition[]>;

    selectedFolder$: Observable<ReminderFolder>;

    selectedFolder: ReminderFolder;

    isThereAnyData$: Observable<boolean>;

    @Input() selected: ActivityItemDefinition;

    @Output() onSelected = new EventEmitter<ActivityItemDefinition>();

    constructor(private _facade: ReminderPoolFacade, private _changeDetector: ChangeDetectorRef) {
        this.folders$ = this._facade.folders$;
        this.selectedFolder$ = this._facade.selected$;
        this.items$ = this._facade.selectedFolderItems$;
        this.isThereAnyData$ = this._facade._isThereAnyData$;
        this.isLoading$ = this._facade.loading$;
        this._facade.root();
    }

    ngOnInit(): void {
        this.selectedFolder$.pipe(takeUntil(this._unsubscribeAll)).subscribe((selected) => {
            this.selectedFolder = selected;
            if (selected) {
                this._facade.loadReminderAction(selected.id);
                this.onSelected.emit(null);
            }
        });

        this.filteredFolders$.pipe(takeUntil(this._unsubscribeAll)).subscribe((folders) => {
            this.reminderFolders = folders.map((folder) => {
                return new ReminderFolderNode(folder, folder.parentFolderId ?? 0);
            });
            this.dataSource.data = this.reminderFolders;
            this.treeControl.expandAll();
            this._changeDetector.markForCheck();
        });

        this.folders$.pipe(takeUntil(this._unsubscribeAll)).subscribe((folders) => {
            this.filteredFolders$.next(folders);
        });

        this.items$.pipe(takeUntil(this._unsubscribeAll)).subscribe((forms) => {
            this.filteredForms$.next(forms);
        });

        this.searchForm
            .get("text")
            .valueChanges.pipe(takeUntil(this._unsubscribeAll))
            .subscribe((text) => {
                this.applyFilter(text.toLowerCase());
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    select(folder: ReminderFolder) {
        this._facade.select(folder.id);
    }

    goBack() {
        this._facade.goBack();
    }

    selectFormDefinition(event: any, definition: ActivityItemDefinition) {
        event.stopPropagation();
        this.onSelected.emit(definition);
    }

    applyFilter(text: string) {
        combineLatest([this.folders$, this.items$])
            .pipe(take(1))
            .subscribe(([folders, forms]) => {
                this.filteredFolders$.next(folders);

                let filteredForms = forms.filter((form) => form.title.toLowerCase().includes(text));
                this.filteredForms$.next(filteredForms);

                let keepSelected: boolean = filteredForms.length > 0;
                let filteredFolders = this.filterFoldersByName(folders, text, keepSelected);
                this.filteredFolders$.next(filteredFolders);
            });
    }

    filterFoldersByName(folders: ReminderFolder[], searchText: string, keepSelected: boolean) {
        let filteredFolders = [];
        folders.forEach((folder) => {
            if (
                folder.name.toLowerCase().includes(searchText) ||
                (keepSelected && folder.id === this.selectedFolder?.id)
            ) {
                filteredFolders.push(folder);
            } else if (folder.folders) {
                let filteredSubFolders = this.filterFoldersByName(folder.folders, searchText, keepSelected);
                if (filteredSubFolders.length > 0) {
                    let filteredFolder = Object.assign({}, folder);
                    filteredFolder.folders = filteredSubFolders;
                    filteredFolders.push(filteredFolder);
                }
            }
        });
        return filteredFolders;
    }
}
